import React from "react";
import { CheckCircle2, MapPin, Briefcase, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { Section, SectionHeading, Eyebrow, PrimaryLink } from "@/components/common";
import { ROLES, BENEFITS, BG } from "@/data/content";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";

export default function Careers() {
  const { t } = useLang();
  return (
    <div>
      <Seo title="Careers" description="Explore future opportunities with ERAM DIGITAL. No active roles are currently advertised; you can introduce yourself through our contact page." path="/careers" />

      <section className="relative overflow-hidden">
        <Section className="relative z-10 py-20">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <Reveal>
              <Eyebrow>Careers</Eyebrow>
              <h1 className="font-display text-5xl font-semibold leading-tight tracking-tighter text-[#0A0A0A] sm:text-6xl">
                Build what the region <span className="gradient-text">runs on.</span>
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-[#4B5563]">
                We're a team of engineers, designers and strategists who care about craft and impact. Join us to
                ship technology that shapes the Gulf's digital future.
              </p>
              <div className="mt-8"><a href="#open-roles" className="group inline-flex items-center gap-2 rounded-full gradient-bg px-7 py-3.5 text-sm font-semibold text-white transition-transform hover:scale-[1.03]" data-testid="careers-view-roles">Explore future opportunities<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></a></div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="overflow-hidden rounded-3xl border border-[#E5E7EB]">
                <img src={BG.team} alt="Team collaboration illustration from the ERAM DIGITAL brochure" width="1024" height="1167" className="aspect-[5/4] w-full object-cover object-center" loading="lazy" data-testid="careers-team-graphic" />
              </div>
            </Reveal>
          </div>
        </Section>
      </section>

      <div className="relative overflow-hidden border-y border-[#E5E7EB] bg-[#F9FAFB]">
        <Section className="relative z-10 py-24">
          <Reveal><SectionHeading eyebrow="What we value" title="A place to do your" gradient="best work." /></Reveal>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b, i) => (
              <Reveal key={b} delay={i * 0.05}>
                <div className="flex items-start gap-3 rounded-2xl border border-[#E5E7EB] bg-white p-6">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-teal" />
                  <span className="text-sm text-[#374151]">{b}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>
      </div>

      <Section id="open-roles" className="scroll-mt-24 py-24">
        <Reveal><SectionHeading eyebrow="Open roles" title="Find your" gradient="next role." /></Reveal>
        <div className="mt-12 space-y-4">
          {ROLES.length === 0 ? (
            <Reveal>
              <div className="rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB] p-10 text-center" data-testid="no-roles">
                <p className="font-display text-xl font-semibold text-[#0A0A0A]">No open roles at the moment.</p>
                <p className="mx-auto mt-2 max-w-lg text-[#4B5563]">We're always glad to meet talented people. Share your details and what you're great at, and we'll reach out when the right role opens.</p>
                <div className="mt-6 flex justify-center"><PrimaryLink to="/contact" testid="no-roles-cta">Introduce yourself</PrimaryLink></div>
              </div>
            </Reveal>
          ) : ROLES.map((r, i) => (
            <Reveal key={r.title} delay={i * 0.04}>
              <div className="group flex flex-col items-start justify-between gap-4 rounded-2xl border border-[#E5E7EB] bg-white p-6 transition-colors hover:border-brand-violet/40 sm:flex-row sm:items-center" data-testid={`role-${i}`}>
                <div>
                  <h4 className="font-display text-lg font-semibold text-[#0A0A0A]">{r.title}</h4>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-[#4B5563]">
                    <span className="flex items-center gap-1.5"><Briefcase className="h-4 w-4" />{r.team}</span>
                    <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{r.location}</span>
                    <span className="rounded-full border border-[#E5E7EB] px-2.5 py-0.5 text-xs">{r.type}</span>
                  </div>
                </div>
                <Link to="/contact" className="inline-flex items-center gap-2 rounded-full border border-[#E5E7EB] px-5 py-2.5 text-sm font-semibold text-[#0A0A0A] transition-colors hover:border-brand-violet hover:bg-[#F3F4F6]" data-testid={`apply-${i}`}>
                  Apply<ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section className="pb-28">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-[#E5E7EB] bg-gradient-to-br from-brand-blue/10 via-brand-violet/10 to-brand-teal/10 p-10 text-center sm:p-14">
            <div className="relative z-10">
              <SectionHeading eyebrow="Don't see a fit?" title="We're always meeting" gradient="great people." center subtitle="Send us your details and tell us what you're great at — we'll reach out when the right role opens." />
              <div className="mt-8 flex justify-center"><PrimaryLink to="/contact" testid="careers-cta">Introduce yourself</PrimaryLink></div>
            </div>
          </div>
        </Reveal>
      </Section>
    </div>
  );
}
